/**
 * Stone Investment Values Section
 * 
 * Company values display with Stone Investment's luxury aesthetic:
 * - Clean grid layout
 * - Icon or number indicators
 * - Editorial typography
 */

'use client';

import type { ValuesSectionProps } from '@routeroyal/theme-core';

/**
 * StoneValuesSection - Company values display
 */
export function StoneValuesSection({
  title,
  subtitle,
  values,
  background = 'white',
  columns = 3
}: ValuesSectionProps) {
  const bgClass = background === 'gray' ? 'bg-stone-100' : 'bg-white';
  
  const columnClasses = {
    2: 'md:grid-cols-2',
    3: 'md:grid-cols-2 lg:grid-cols-3',
    4: 'md:grid-cols-2 lg:grid-cols-4',
  };
  
  return (
    <section className={`py-20 lg:py-32 ${bgClass}`}>
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="mb-16 max-w-2xl">
          <p className="text-[10px] uppercase tracking-[0.3em] text-stone-500 font-sans mb-4">
            Our Values
          </p>
          <h2 
            className="font-serif text-4xl lg:text-5xl text-stone-900 mb-6"
            style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
          >
            {title}
          </h2>
          
          {subtitle && (
            <p 
              className="text-lg text-stone-600 font-sans"
              style={{ fontFamily: "'Montserrat', sans-serif" }}
            >
              {subtitle}
            </p>
          )}
        </div>
        
        {/* Values Grid */}
        <div className={`grid grid-cols-1 ${columnClasses[columns]} gap-12 lg:gap-16`}>
          {values.map((value, index) => (
            <div key={index} className="group">
              {/* Number or Icon */}
              <div className="mb-6">
                {value.icon ? (
                  typeof value.icon === 'string' ? (
                    <span className="text-3xl">{value.icon}</span>
                  ) : (
                    value.icon
                  )
                ) : (
                  <span 
                    className="font-serif text-5xl text-stone-300 group-hover:text-stone-400 transition-colors"
                    style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
                  >
                    {String(index + 1).padStart(2, '0')}
                  </span>
                )}
              </div>
              
              {/* Title */}
              <h3 
                className="font-serif text-2xl text-stone-900 mb-4"
                style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
              >
                {value.title}
              </h3>
              
              {/* Description */}
              <p 
                className="text-stone-600 leading-relaxed font-sans text-sm"
                style={{ fontFamily: "'Montserrat', sans-serif" }}
              >
                {value.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default StoneValuesSection;
